import { AuthContextProvider, BaseApiClient, Channel, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { AllowedProvider } from '../constants/common';

type ChannelsAccessParams = {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
};

type ChannelsAccessResponse = {
  channelId: string;
  username: string;
  avatar: string;
  provider: AllowedProvider;
  role: 'owner' | 'administrator' | 'editor' | 'botManager';
}[];

export class ChannelsAccessApiClient extends BaseApiClient {
  constructor({ authContextProvider, serviceEnvironment }: ChannelsAccessParams) {
    super({
      authContextProvider,
      baseURL: `${serviceEnvironment.kappaV2ApiBaseUrl}`,
    });
  }

  public impersonateChannel({ channelId }: { channelId: string }): Promise<{
    isOwner: boolean;
    roleplaying: boolean;
    nam: string;
    token: string;
    tokenV2: string;
    opts: {
      domain: string;
      maxAge: number;
      path: string;
      secure: boolean;
    };
  }> {
    return this.post(`/channels/${channelId}/roleplay`);
  }

  /**
   * This exists because of the ridiculous caching in the SDK which can overflow
   * if a user has access to a lot of channels, which is not uncommon for mods of large users.
   */
  public fetchAccessToChannels(): Promise<ChannelsAccessResponse> {
    return this.get('/users/access');
  }

  public fetchChannelDetails({ channelId, token }: { channelId: string; token: string }): Promise<Channel> {
    return this.get(`/channels/${channelId}/details`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  public async dispose() {
    super.dispose();
  }
}

export const channelsOwnerAccessQueryOptions = ({
  authContextProvider,
  serviceEnvironment,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
}) =>
  queryOptions({
    queryKey: ['channel', 'access', 'owner'],
    queryFn: async () => {
      const channelsAccessApiClient = new ChannelsAccessApiClient({
        authContextProvider,
        serviceEnvironment,
      });
      const authContext = await authContextProvider.getAuthContext();
      const channels = await channelsAccessApiClient.fetchAccessToChannels();

      return Promise.all(
        channels
          .filter((channel) => {
            if (channel.role !== 'owner') return false;

            switch (channel.provider) {
              case 'twitch':
              case 'youtube':
              case 'x':
              case 'kick':
                // case 'trovo':
                return true;
            }

            return false;
          })
          .map((channel) => ({
            ...channel,
            // Some channels are completely fucked and are missing crucial properties, such as username
            // or displayName. This ensures we have the property;
            // In case something goes extremely wrong, we say "Failed to load" so they reach out to support.
            username: (channel?.username || authContext?.channel?.username)?.toLocaleLowerCase() ?? 'Failed to load',
          }))
      );
    },
  });

export const authContextQueryOptions = ({ authContextProvider }: { authContextProvider: AuthContextProvider }) =>
  queryOptions({
    queryKey: ['authContext'],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      return authContextProvider.getAuthContext();
    },
  });
